import { useEffect, useId, useRef } from 'react';
import { Z_DESKTOP_ATTRACT } from '../../../features/quickstart/config/zIndexLayers';

interface DesktopControlsAttractOverlayProps {
  visible: boolean;
  /**
   * Called exactly once on the first WASD keypress or mouse click while visible.
   * Parent is responsible for requesting browser locks (fullscreen/pointer/keyboard)
   * and transitioning out of the attract state. Must run synchronously from the
   * user-gesture handler so lock APIs retain user activation.
   */
  onFirstInput: () => void;
}

const KEYFRAMES_ID = 'desktop-controls-attract-keyframes';

/*
 * One shared 1500ms cycle keeps the W press, mouse left-click + counter-clockwise
 * rotation, and finger tap perfectly phase-locked. AC #5 / #7 specify the active
 * press animation totals 300ms, broken down as 100ms in + 100ms hold + 100ms out:
 *   0% – 53.3%    (0–800ms)    rest
 *   53.3% – 60%   (800–900ms)  press transition (100ms)
 *   60% – 66.6%   (900–1000ms) pressed hold (100ms)
 *   66.6% – 73.3% (1000–1100ms) release transition (100ms)
 *   73.3% – 100%  (1100–1500ms) rest
 * The hand runs a one-shot enter from off-screen first, then joins the loop with
 * a matching 1500ms delay so the very first tap lands on the very first click.
 * The hand snaps in (50ms) and out (50ms) to feel instant per prior feedback,
 * while sharing the same 66.6% release point as the mouse left-click fill.
 */
const keyframesCSS = `
@keyframes dca-w-press {
  0%, 53.3% { transform: translate(0, 0); }
  60%, 66.6% { transform: translate(2px, 2px); }
  73.3%, 100% { transform: translate(0, 0); }
}
@keyframes dca-w-bg {
  0%, 53.3% { fill: rgba(68, 68, 68, 0.5); }
  60%, 66.6% { fill: rgba(145, 145, 145, 0.5); }
  73.3%, 100% { fill: rgba(68, 68, 68, 0.5); }
}
@keyframes dca-mouse-rotate {
  0%, 53.3% { transform: rotate(-15.98deg); }
  60%, 66.6% { transform: rotate(-19.98deg); }
  73.3%, 100% { transform: rotate(-15.98deg); }
}
@keyframes dca-mouse-leftclick-fill {
  0%, 53.3% { fill: rgba(68, 68, 68, 0.5); }
  60%, 66.6% { fill: #FBBF24; }
  73.3%, 100% { fill: rgba(68, 68, 68, 0.5); }
}
@keyframes dca-hand-enter {
  0% { transform: translate(60vw, 60vh); opacity: 0; }
  100% { transform: translate(0, 0); opacity: 1; }
}
@keyframes dca-hand-tap {
  0%, 53.3% { transform: rotate(-8.29deg) scale(1); filter: brightness(1) drop-shadow(0 4px 8px rgba(0, 0, 0, 0.55)); }
  56.6%, 66.6% { transform: rotate(-8.29deg) scale(0.95); filter: brightness(0.72) drop-shadow(0 2px 4px rgba(0, 0, 0, 0.55)); }
  70%, 100% { transform: rotate(-8.29deg) scale(1); filter: brightness(1) drop-shadow(0 4px 8px rgba(0, 0, 0, 0.55)); }
}
`;

function useDesktopControlsKeyframes() {
  useEffect(() => {
    // Always sync textContent so edits to `keyframesCSS` propagate across
    // Vite/HMR remounts. The original early-return-if-exists pattern caused
    // updated keyframes to be silently ignored after a hot reload because the
    // <style> tag from the previous mount is still in <head>.
    let style = document.getElementById(KEYFRAMES_ID) as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement('style');
      style.id = KEYFRAMES_ID;
      document.head.appendChild(style);
    }
    if (style.textContent !== keyframesCSS) {
      style.textContent = keyframesCSS;
    }
    // Intentionally no cleanup: this style element is a shared, idempotent
    // singleton keyed by KEYFRAMES_ID. Removing it on unmount can break a
    // concurrently mounted instance (e.g. StrictMode double-invoke or rapid
    // remount, where instance B reuses the tag instance A inserted). Leaving
    // it in <head> until page unload is harmless.
  }, []);
}

const BOTTOM_ROW_KEYS = ['A', 'S', 'D'] as const;

/**
 * Figma KeyButton component (file IkWjO0GHFgAkphmkoF7Mzl, node 4468:1250).
 * Canonical source-of-truth lives at apps/marketing-demo/public/KeyButton.svg;
 * this JSX is a faithful re-rendering of that file. Three stacked rects on a
 * shared 142x142 rounded frame (rx 15):
 *   1. Interior fill — #444444 50% at rest, animated for the W press flash
 *      via dca-w-bg up to #919191 50% (both values lifted directly from the
 *      KeyButton.svg and KeyButton (2).svg Figma exports in public/). The
 *      rest fill is kept off the inline `style` object so the keyframe-driven
 *      `fill` value always wins (some browsers shadow CSS animations behind
 *      inline-style fill). The flash is intentionally subtle — only ~50%
 *      brighter than rest, so it reads as a tap rather than a stark white
 *      flash. For it to be visible during dev, useDesktopControlsKeyframes
 *      must overwrite the injected <style> on every mount (HMR can otherwise
 *      leave stale CSS content in the document and silently swallow keyframe
 *      edits).
 *   2. Solid #444444 50% stroke (3px) — the inner border tone.
 *   3. Diagonal linear-gradient stroke (white 0.25 -> #666666 -> #1E1E1E at
 *      opacity 0.2, 3px) — the outer highlight ring.
 * The Figma drop-shadow filter (offset 2,2, blur 0.9, alpha 0.25) is applied
 * to the group so the shadow only attaches to the rect (not the letter glyph)
 * and protrudes past the SVG bounds thanks to `overflow: visible`.
 *
 * useId() namespaces the filter and gradient ids so the four on-screen KeyCaps
 * never collide (SVG url(#id) refs are document-scoped). The letter is layered
 * as absolutely-positioned text on top of the SVG so HTML text rendering
 * (kerning, system font fallback) is preserved. dca-w-press rides only on the
 * letter overlay — per Figma, the shell (border, drop shadow, rect outline)
 * stays put during the press, only the interior rect fill flashes (dca-w-bg)
 * and the glyph slides a couple pixels down-right.
 */
const KeyCap = ({ letter, animated }: { letter: string; animated?: boolean }) => {
  const reactId = useId();
  const filterId = `dca-key-filter-${reactId}`;
  const gradientId = `dca-key-gradient-${reactId}`;

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
      }}
    >
      <svg
        viewBox='0 0 149 149'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
        style={{ width: '100%', height: '100%', overflow: 'visible', display: 'block' }}
      >
        <g filter={`url(#${filterId})`}>
          <rect
            x='1.5'
            y='1.5'
            width='142'
            height='142'
            rx='15'
            fill='rgba(68, 68, 68, 0.5)'
            shapeRendering='crispEdges'
            style={animated ? { animation: 'dca-w-bg 1500ms linear 1500ms infinite both' } : undefined}
          />
          <rect
            x='1.5'
            y='1.5'
            width='142'
            height='142'
            rx='15'
            fill='none'
            stroke='#444444'
            strokeOpacity={0.5}
            strokeWidth={3}
            shapeRendering='crispEdges'
          />
          <rect
            x='1.5'
            y='1.5'
            width='142'
            height='142'
            rx='15'
            fill='none'
            stroke={`url(#${gradientId})`}
            strokeOpacity={0.2}
            strokeWidth={3}
            shapeRendering='crispEdges'
          />
        </g>
        <defs>
          <filter
            id={filterId}
            x='0'
            y='0'
            width='148.8'
            height='148.8'
            filterUnits='userSpaceOnUse'
            colorInterpolationFilters='sRGB'
          >
            <feFlood floodOpacity={0} result='BackgroundImageFix' />
            <feColorMatrix
              in='SourceAlpha'
              type='matrix'
              values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
              result='hardAlpha'
            />
            <feOffset dx={2} dy={2} />
            <feGaussianBlur stdDeviation={0.9} />
            <feComposite in2='hardAlpha' operator='out' />
            <feColorMatrix type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0' />
            <feBlend mode='normal' in2='BackgroundImageFix' result='effect1_dropShadow' />
            <feBlend mode='normal' in='SourceGraphic' in2='effect1_dropShadow' result='shape' />
          </filter>
          <linearGradient
            id={gradientId}
            x1='7.5'
            y1='7.5'
            x2='143.5'
            y2='143.466'
            gradientUnits='userSpaceOnUse'
          >
            <stop stopColor='white' stopOpacity={0.25} />
            <stop offset={0.25} stopColor='#666666' />
            <stop offset={0.5} stopColor='#1E1E1E' />
          </linearGradient>
        </defs>
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'Inter, system-ui, sans-serif',
          fontWeight: 700,
          fontSize: 'min(5vw, 8.89vh)',
          color: 'rgba(224, 231, 255, 0.5)',
          lineHeight: 1,
          pointerEvents: 'none',
          animation: animated ? 'dca-w-press 1500ms linear 1500ms infinite both' : undefined,
        }}
      >
        {letter}
      </div>
    </div>
  );
};

/**
 * Mouse silhouette imported from the Figma "Mouse" component
 * (file IkWjO0GHFgAkphmkoF7Mzl, node 4468:993). Canonical source-of-truth
 * lives at apps/marketing-demo/public/mouse.svg; this JSX is a faithful
 * re-rendering of that file with one structural deviation: the compound
 * fill path is split into three independent <path> elements so we can flash
 * just the left-click region yellow (#FBBF24) during the pressed window
 * while body / right-click stay grey. The single compound stroke path with
 * Figma's diagonal gradient is preserved verbatim. The drop-shadow filter
 * from Figma's source is intentionally omitted - the wrapper's CSS
 * `filter: drop-shadow(...)` already handles it; including it here would
 * double the shadow.
 *
 * The gradient id is namespaced via useId() (matching KeyCap) so that if
 * MouseSvg is ever reused — multiple mouse instances on the same screen,
 * Storybook stories rendering several variants side-by-side, etc. — the
 * SVG url(#id) refs (document-scoped) never collide. Only one mouse is
 * mounted today, but the cost is one extra hook call and zero ambiguity.
 */
const MouseSvg = () => {
  const reactId = useId();
  const gradientId = `dca-mouse-border-gradient-${reactId}`;

  return (
    <svg
      viewBox='1.5 1.5 203 271'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      style={{ width: '100%', height: '100%', overflow: 'visible' }}
    >
      <path
        d='M1.5 103.125H94.5417V1.5H86.0833C39.351 1.5 1.5 39.3977 1.5 86.1875V103.125Z'
        shapeRendering='crispEdges'
        style={{
          fill: 'rgba(68, 68, 68, 0.5)',
          animation: 'dca-mouse-leftclick-fill 1500ms linear 1500ms infinite both',
        }}
      />
      <path
        d='M1.5 120.062V187.812C1.5 234.602 39.351 272.5 86.0833 272.5H119.917C166.649 272.5 204.5 234.602 204.5 187.812V120.062H103H1.5Z'
        fill='rgba(68, 68, 68, 0.5)'
        shapeRendering='crispEdges'
      />
      <path
        d='M204.5 103.125V86.1875C204.5 39.3977 166.649 1.5 119.917 1.5H111.458V103.125H204.5Z'
        fill='rgba(68, 68, 68, 0.5)'
        shapeRendering='crispEdges'
      />
      <path
        d='M1.5 103.125H94.5417V1.5H86.0833C39.351 1.5 1.5 39.3977 1.5 86.1875V103.125Z M1.5 120.062V187.812C1.5 234.602 39.351 272.5 86.0833 272.5H119.917C166.649 272.5 204.5 234.602 204.5 187.812V120.062H103H1.5Z M204.5 103.125V86.1875C204.5 39.3977 166.649 1.5 119.917 1.5H111.458V103.125H204.5Z'
        stroke={`url(#${gradientId})`}
        strokeOpacity={0.2}
        strokeWidth={3}
        fill='none'
        shapeRendering='crispEdges'
      />
      <defs>
        <linearGradient
          id={gradientId}
          x1='1.5'
          y1='1.5'
          x2='204.5'
          y2='272.5'
          gradientUnits='userSpaceOnUse'
        >
          <stop stopColor='white' stopOpacity={0.25} />
          <stop offset='0.25' stopColor='#666666' />
          <stop offset='0.5' stopColor='#1E1E1E' />
        </linearGradient>
      </defs>
    </svg>
  );
};

const HandIcon = () => (
  <svg
    viewBox='0 0 135 170'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    style={{
      width: '100%',
      height: '100%',
      overflow: 'visible',
      transformOrigin: 'center center',
      animation: 'dca-hand-tap 1500ms linear 1500ms infinite both',
    }}
  >
    <path
      d='M42.5 2.5C50.787 2.5 57.5 9.21304 57.5 17.5V59.1807C59.7737 58.1023 62.3165 57.5 65 57.5C72.7943 57.5 79.3908 62.5787 81.665 69.6113C84.1349 68.2712 86.9693 67.5 90 67.5C97.7943 67.5 104.391 72.5787 106.665 79.6113C109.135 78.2712 111.969 77.5 115 77.5C124.662 77.5 132.5 85.338 132.5 95V125C132.5 148.474 113.474 167.5 90 167.5H89.998L53.0918 167.469V167.468C40.8197 167.467 28.9416 163.17 19.4932 155.296L17.8066 153.891L17.3555 153.51C7.93782 145.449 2.5 133.671 2.5 121.25V109.375C2.5 102.698 5.45267 96.3701 10.584 92.082L10.5869 92.0791L16.5869 87.0791L16.6123 87.0586L16.6387 87.0381C17.1481 86.6359 17.863 86.0383 18.5664 85.5459L22.5 82.792V105C22.5 106.369 23.6307 107.5 25 107.5C26.3693 107.5 27.5 106.369 27.5 105V17.5C27.5 9.21304 34.213 2.5 42.5 2.5Z'
      fill='white'
      stroke='black'
      strokeWidth='5'
    />
  </svg>
);

export const DesktopControlsAttractOverlay = ({ visible, onFirstInput }: DesktopControlsAttractOverlayProps) => {
  useDesktopControlsKeyframes();
  const firedRef = useRef(false);

  useEffect(() => {
    if (!visible) {
      firedRef.current = false;
      return;
    }

    const fire = (event: Event) => {
      if (firedRef.current) return;
      firedRef.current = true;
      // Stop the dismissing keystroke/click from reaching the streaming game
      // (matches PlayNowOverlay where the dismiss-click is consumed by the button).
      event.preventDefault();
      event.stopPropagation();
      onFirstInput();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      if (key === 'w' || key === 'a' || key === 's' || key === 'd') {
        fire(event);
      }
    };

    const handleMouseDown = (event: MouseEvent) => {
      fire(event);
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true });
    window.addEventListener('mousedown', handleMouseDown, { capture: true });

    return () => {
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
      window.removeEventListener('mousedown', handleMouseDown, { capture: true });
    };
  }, [visible, onFirstInput]);

  if (!visible) return null;

  return (
    <div className='pointer-events-none fixed inset-0 flex items-center justify-center' style={{ zIndex: Z_DESKTOP_ATTRACT }}>
      {/* 16:9 stage that scales to fit the viewport. Children are positioned in
          percentages of the 1920x1080 Figma reference frame (node 4467:942). */}
      <div
        className='relative'
        style={{
          aspectRatio: '1920 / 1080',
          width: '100vw',
          maxWidth: 'calc(100vh * 16 / 9)',
          height: 'auto',
        }}
      >
        {/* WASD bed (Figma instance 4468:1226 at 150,546 sized 466x304) */}
        <div
          style={{
            position: 'absolute',
            left: '7.81%',
            top: '50.56%',
            width: '24.27%',
            height: '28.15%',
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gridTemplateRows: 'repeat(2, 1fr)',
            columnGap: '4.3%',
            rowGap: '6.58%',
          }}
        >
          <div style={{ gridColumn: 1, gridRow: 1 }} />
          <div style={{ gridColumn: 2, gridRow: 1 }}>
            <KeyCap letter='W' animated />
          </div>
          <div style={{ gridColumn: 3, gridRow: 1 }} />
          {BOTTOM_ROW_KEYS.map((letter, index) => (
            <div key={letter} style={{ gridColumn: index + 1, gridRow: 2 }}>
              <KeyCap letter={letter} />
            </div>
          ))}
        </div>

        {/* Mouse (Figma instance 4468:1001 at 1541.6,612.19 sized 269.8x316.4).
            Figma layout panel records the resting rotation as 15.98deg; the
            AC's "counter-clockwise 4deg on press" is applied relative to it
            (15.98 -> 11.98). Inline transform is the static pre-loop fallback
            during the 1500ms entrance delay before dca-mouse-rotate takes over. */}
        <div
          style={{
            position: 'absolute',
            left: '82.03%',
            top: '58.79%',
            width: '10.57%',
            height: '25.09%',
            transform: 'rotate(-15.98deg)',
            animation: 'dca-mouse-rotate 1500ms linear 1500ms infinite both',
            transformOrigin: 'center center',
            filter: 'drop-shadow(2px 2px 1.8px rgba(0, 0, 0, 0.25))',
          }}
        >
          <MouseSvg />
        </div>

        {/* Finger / pointer (Figma instance 4483:1364 at 1481,425 sized 708x708,
            with the hand visual itself at 95.88,209.28 sized 125x160 inside that
            component). Hand enters once from off-screen, then loops a tap
            synchronized with the mouse left-click animation. Rotation pivots
            around the hand's bounding-box center to match Figma's 8.29deg
            rotation pivot. */}
        <div
          style={{
            position: 'absolute',
            left: '82.53%',
            top: '64.63%',
            width: '6.51%',
            height: '14.81%',
            animation: 'dca-hand-enter 1500ms cubic-bezier(0.22, 1, 0.36, 1) 0s both',
          }}
        >
          <HandIcon />
        </div>
      </div>
    </div>
  );
};
